import { Box } from '@mui/material';

import { ParticipantTile } from './ParticipantTile';

interface SpeakerViewProps {
  mainStageSessionId: string | null;
  sidebarParticipantIds: string[];
  activeSpeakerId: string | null;
  localSessionId: string;
  isScreenShare?: boolean;
  screenShareSessionId?: string | null;
  fillHeight?: boolean;
}

export function SpeakerView({
  mainStageSessionId,
  sidebarParticipantIds,
  activeSpeakerId,
  localSessionId,
  isScreenShare = false,
  screenShareSessionId,
  fillHeight = false,
}: SpeakerViewProps) {
  const mainSessionId =
    isScreenShare && screenShareSessionId ? screenShareSessionId : mainStageSessionId;

  return (
    // Outer box: size container for @container queries (cannot query itself)
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(fillHeight ? { flex: 1, minHeight: 0, height: '100%' } : { aspectRatio: '16 / 9' }),
        overflow: 'hidden',
        containerType: 'size',
      }}
    >
      {/* Inner layout — responds to @container queries from the outer box */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          '@container (max-aspect-ratio: 4/3)': {
            flexDirection: 'column',
          },
        }}
      >
        {/* Sidebar — renders after main stage in DOM but moves above it when narrow */}
        {sidebarParticipantIds.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '20%',
              minWidth: 150,
              flexShrink: 0,
              overflowY: 'auto',
              pl: 1.5,
              order: 1,
              '@container (max-aspect-ratio: 4/3)': {
                order: -1,
                flexDirection: 'row',
                alignItems: 'flex-start',
                width: '100%',
                minWidth: 0,
                flexShrink: 0,
                pl: 0,
                pb: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                '& > *': {
                  width: '20%',
                  minWidth: 150,
                  flexShrink: 0,
                },
              },
            }}
          >
            {sidebarParticipantIds.map(id => (
              <ParticipantTile
                key={id}
                sessionId={id}
                variant="thumbnail"
                isLocal={id === localSessionId}
                isActiveSpeaker={id === activeSpeakerId}
              />
            ))}
          </Box>
        )}

        {/* Main stage */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {mainSessionId && (
            <ParticipantTile
              sessionId={mainSessionId}
              variant="main"
              isLocal={mainSessionId === localSessionId}
              isActiveSpeaker={mainSessionId === activeSpeakerId}
              isScreenShare={isScreenShare}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
